<script setup>

import {computed, ref} from "vue";
import FourPointStar from "../icons/fourPointStar.vue";
import { browserStorage, savePlanPreset } from "../../utils/planPreset.mjs";

const props = defineProps({
    uuid: String,
    webinarId: {
        type: [String, Number],
        default: null,
    },
    webinarDetails: {
        type: Object,
        default: null,
    },
    paidPlan: {
        type: Boolean,
        default: false,
    },
    welcome: {
        type: Object,
        default: null,
    },
});

savePlanPreset(browserStorage(), window.location.search);

const messages = {
    en: {
        paid_banner: 'Heads up: this invitation requires a paid subscription to sign up.',
        free_prop_banner: '🎁 6 months free — no credit card required',
        invited_by: 'Personally invited by {name}',
        cta_buy: 'Buy now (From €19)',
        cta_free: 'Join for free',
        cta_disclaimer_paid: 'A paid subscription is required to sign up.',
        cta_disclaimer_free: '6 months Basic included. No credit card required.',
        upcoming_webinar: 'Upcoming Webinar',
        hero_heading: 'Simplify & grow your business',
        hero_badge_html: '<span class="text-gray-500">Made for </span><span class="text-myflowPurple">solopreneurs &amp; small teams</span>',
        hero_body: 'Manage customers, marketing and sales, in one place. Use the tools yourself, or ask your AI teammates to do it for you. No hiring. No salaries. Just results.',
        video_caption: 'myflow in 1 minute.',
        flowie_quickstart_heading: 'Flowie helps you get started in minutes',
        flowie_quickstart_html: 'Register your free account and meet Flowie - your new Business Assistant that helps you get things done, faster. <strong class="font-bold">Simply ask, Flowie & the teammates will deliver.</strong>',
        flowie_input_placeholder: 'Ask Flowie anything...',
        flowie_prompt_send: 'Send',
        flowie_prompt_new_customers: 'I want to find new customers',
        flowie_prompt_new_website: 'I want a new website',
        flowie_prompt_send_newsletter: 'I want to send a newsletter',
        flowie_prompt_create_content: 'I want help to create content',
        flowie_prompt_automatic_emails: 'I want to send automatic emails',
        flowie_prompt_customer_needs: 'I want to know what my customers need',
        flowie_prompt_time_saving_crm: 'I want a CRM that saves me time',
        meet_team_heading: 'Meet your new AI team',
        meet_team_body_html: 'Your new teammates will help you; build websites, manage contacts, find new leads, write newsletters, create social media posts, craft quotes, and much more. <strong class="font-bold">You ask. They deliver.</strong>',
        features_and_more: 'and more',
        footer_trust: 'Trusted by thousands of companies around the world.',
        footer_founded: 'Founded in Sweden. Available in 170+ countries.',
        webinar_duration_minutes: '{n} minutes',
    },
    sv: {
        paid_banner: 'Obs: den här inbjudan kräver ett betalt abonnemang för att registrera dig.',
        free_prop_banner: '🎁 6 månader gratis — inget kreditkort krävs',
        invited_by: 'Personligen inbjuden av {name}',
        cta_buy: 'Köp nu (Från €19)',
        cta_free: 'Gå med gratis',
        cta_disclaimer_paid: 'Ett betalt abonnemang krävs för att registrera sig.',
        cta_disclaimer_free: '6 månader Basic ingår. Inget kreditkort krävs.',
        upcoming_webinar: 'Kommande Webbinarium',
        hero_heading: 'Förenkla & väx ditt företag',
        hero_badge_html: '<span class="text-gray-500">Gjord för </span><span class="text-myflowPurple">soloföretagare &amp; små team</span>',
        hero_body: 'Hantera kunder, marknadsföring och försäljning på ett ställe. Använd verktygen själv, eller be dina AI-teammedlemmar göra det åt dig. Inga anställningar. Inga löner. Bara resultat.',
        video_caption: 'myflow på 1 minut.',
        flowie_quickstart_heading: 'Flowie hjälper dig att komma igång på några minuter',
        flowie_quickstart_html: 'Registrera ditt kostnadsfria konto och möt Flowie – din nya företagsassistent som hjälper dig att få saker gjorda snabbare. <strong class="font-bold">Fråga bara, så levererar Flowie och teammedlemmarna.</strong>',
        flowie_input_placeholder: 'Fråga Flowie vad som helst...',
        flowie_prompt_send: 'Skicka',
        flowie_prompt_new_customers: 'Jag vill hitta nya kunder',
        flowie_prompt_new_website: 'Jag vill ha en ny webbplats',
        flowie_prompt_send_newsletter: 'Jag vill skicka ett nyhetsbrev',
        flowie_prompt_create_content: 'Jag vill ha hjälp att skapa innehåll',
        flowie_prompt_automatic_emails: 'Jag vill skicka automatiska e-postmeddelanden',
        flowie_prompt_customer_needs: 'Jag vill veta vad mina kunder behöver',
        flowie_prompt_time_saving_crm: 'Jag vill ha ett CRM som sparar mig tid',
        meet_team_heading: 'Möt ditt nya AI-team',
        meet_team_body_html: 'Dina nya teammedlemmar hjälper dig att: bygga webbplatser, hantera kontakter, hitta nya leads, skriva nyhetsbrev, skapa inlägg för sociala medier, ta fram offerter och mycket mer. <strong class="font-bold">Du frågar. De levererar.</strong>',
        features_and_more: 'med mera',
        footer_trust: 'Betrodd av tusentals företag runt om i världen.',
        footer_founded: 'Grundat i Sverige. Tillgängligt i 170+ länder.',
        webinar_duration_minutes: '{n} minuter',
    },
    no: {
        paid_banner: 'Merk: denne invitasjonen krever et betalt abonnement for å registrere deg.',
        free_prop_banner: '🎁 6 måneder gratis — ingen kredittkort kreves',
        invited_by: 'Personlig invitert av {name}',
        cta_buy: 'Kjøp nå (Fra €19)',
        cta_free: 'Bli med gratis',
        cta_disclaimer_paid: 'Et betalt abonnement kreves for å registrere seg.',
        cta_disclaimer_free: '6 måneder Basic inkludert. Ingen kredittkort kreves.',
        upcoming_webinar: 'Kommende Webinar',
        hero_heading: 'Forenkle & vokse virksomheten din',
        hero_badge_html: '<span class="text-gray-500">Laget for </span><span class="text-myflowPurple">solopreneurer &amp; små team</span>',
        hero_body: 'Administrer kunder, markedsføring og salg på ett sted. Bruk verktøyene selv, eller be AI-teammedlemmene dine gjøre det for deg. Ingen ansettelser. Ingen lønninger. Bare resultater.',
        video_caption: 'myflow på 1 minutt.',
        flowie_quickstart_heading: 'Flowie hjelper deg i gang på få minutter',
        flowie_quickstart_html: 'Registrer gratiskontoen din og møt Flowie – din nye bedriftsassistent som hjelper deg med å få ting gjort raskere. <strong class="font-bold">Bare spør, så leverer Flowie og teammedlemmene.</strong>',
        flowie_input_placeholder: 'Spør Flowie om hva som helst...',
        flowie_prompt_send: 'Send',
        flowie_prompt_new_customers: 'Jeg vil finne nye kunder',
        flowie_prompt_new_website: 'Jeg vil ha et nytt nettsted',
        flowie_prompt_send_newsletter: 'Jeg vil sende et nyhetsbrev',
        flowie_prompt_create_content: 'Jeg vil ha hjelp til å lage innhold',
        flowie_prompt_automatic_emails: 'Jeg vil sende automatiske e-poster',
        flowie_prompt_customer_needs: 'Jeg vil vite hva kundene mine trenger',
        flowie_prompt_time_saving_crm: 'Jeg vil ha et CRM som sparer meg tid',
        meet_team_heading: 'Møt det nye AI-teamet ditt',
        meet_team_body_html: 'De nye teammedlemmene dine hjelper deg med å: bygge nettsider, administrere kontakter, finne nye leads, skrive nyhetsbrev, lage innlegg for sosiale medier, lage tilbud og mye mer. <strong class="font-bold">Du spør. De leverer.</strong>',
        features_and_more: 'med mer',
        footer_trust: 'Stolt på av tusenvis av selskaper over hele verden.',
        footer_founded: 'Grunnlagt i Sverige. Tilgjengelig i 170+ land.',
        webinar_duration_minutes: '{n} minutter',
    },
    de: {
        paid_banner: 'Hinweis: Für diese Einladung ist ein kostenpflichtiges Abonnement erforderlich, um dich zu registrieren.',
        free_prop_banner: '🎁 6 Monate kostenlos — keine Kreditkarte erforderlich',
        invited_by: 'Persönlich eingeladen von {name}',
        cta_buy: 'Jetzt kaufen (ab €19)',
        cta_free: 'Kostenlos beitreten',
        cta_disclaimer_paid: 'Ein kostenpflichtiges Abonnement ist für die Registrierung erforderlich.',
        cta_disclaimer_free: '6 Monate Basic inklusive. Keine Kreditkarte erforderlich.',
        upcoming_webinar: 'Kommendes Webinar',
        hero_heading: 'Vereinfache & skaliere dein Unternehmen',
        hero_badge_html: '<span class="text-gray-500">Gemacht für </span><span class="text-myflowPurple">Soloselbstständige &amp; kleine Teams</span>',
        hero_body: 'Verwalte Kunden, Marketing und Vertrieb an einem Ort. Nutze die Tools selbst oder bitte deine KI-Teammitglieder, es für dich zu erledigen. Keine Einstellungen. Keine Gehälter. Nur Ergebnisse.',
        video_caption: 'myflow in 1 Minute.',
        flowie_quickstart_heading: 'Mit Flowie legst du in wenigen Minuten los',
        flowie_quickstart_html: 'Registriere dein kostenloses Konto und lerne Flowie kennen – deinen neuen Business-Assistenten, mit dem du Aufgaben schneller erledigst. <strong class="font-bold">Frag einfach, Flowie und die Teammitglieder liefern.</strong>',
        flowie_input_placeholder: 'Fragen Sie Flowie alles...',
        flowie_prompt_send: 'Senden',
        flowie_prompt_new_customers: 'Ich möchte neue Kunden finden',
        flowie_prompt_new_website: 'Ich möchte eine neue Website',
        flowie_prompt_send_newsletter: 'Ich möchte einen Newsletter versenden',
        flowie_prompt_create_content: 'Ich möchte Hilfe beim Erstellen von Inhalten',
        flowie_prompt_automatic_emails: 'Ich möchte automatische E-Mails senden',
        flowie_prompt_customer_needs: 'Ich möchte wissen, was meine Kunden brauchen',
        flowie_prompt_time_saving_crm: 'Ich möchte ein CRM, das mir Zeit spart',
        meet_team_heading: 'Lerne dein neues KI-Team kennen',
        meet_team_body_html: 'Deine neuen Teammitglieder helfen dir dabei, Websites zu erstellen, Kontakte zu verwalten, neue Leads zu finden, Newsletter zu schreiben, Social-Media-Beiträge zu erstellen, Angebote auszuarbeiten und vieles mehr. <strong class="font-bold">Du fragst. Sie liefern.</strong>',
        features_and_more: 'und mehr',
        footer_trust: 'Tausende Unternehmen weltweit vertrauen uns.',
        footer_founded: 'Gegründet in Schweden. Verfügbar in 170+ Ländern.',
        webinar_duration_minutes: '{n} Minuten',
    },
    es: {
        paid_banner: 'Atención: esta invitación requiere una suscripción de pago para registrarte.',
        free_prop_banner: '🎁 6 meses gratis — no se requiere tarjeta de crédito',
        invited_by: 'Invitación personal de {name}',
        cta_buy: 'Comprar ahora (Desde 19 €)',
        cta_free: 'Únete gratis',
        cta_disclaimer_paid: 'Se requiere una suscripción de pago para registrarte.',
        cta_disclaimer_free: 'Incluye 6 meses del plan Basic. No se requiere tarjeta de crédito.',
        upcoming_webinar: 'Próximo webinar',
        hero_heading: 'Simplifica y haz crecer tu negocio',
        hero_badge_html: '<span class="text-gray-500">Creado para </span><span class="text-myflowPurple">emprendedores independientes y equipos pequeños</span>',
        hero_body: 'Gestiona tus clientes, el marketing y las ventas en un solo lugar. Utiliza las herramientas tú mismo o pide a tus compañeros de equipo de IA que lo hagan por ti. Sin contrataciones. Sin salarios. Solo resultados.',
        video_caption: 'myflow en 1 minuto.',
        flowie_quickstart_heading: 'Flowie te ayuda a empezar en cuestión de minutos',
        flowie_quickstart_html: 'Registra tu cuenta gratuita y conoce a Flowie, tu nuevo asistente empresarial que te ayuda a hacer las cosas más rápido. <strong class="font-bold">Solo tienes que pedirlo, y Flowie y sus compañeros de equipo se encargarán.</strong>',
        flowie_input_placeholder: 'Pregúntale lo que quieras a Flowie...',
        flowie_prompt_send: 'Enviar',
        flowie_prompt_new_customers: 'Quiero encontrar nuevos clientes',
        flowie_prompt_new_website: 'Quiero un sitio web nuevo',
        flowie_prompt_send_newsletter: 'Quiero enviar un boletín informativo',
        flowie_prompt_create_content: 'Quiero ayuda para crear contenido',
        flowie_prompt_automatic_emails: 'Quiero enviar correos electrónicos automáticos',
        flowie_prompt_customer_needs: 'Quiero saber qué necesitan mis clientes',
        flowie_prompt_time_saving_crm: 'Quiero un CRM que me ahorre tiempo',
        meet_team_heading: 'Conoce a tu nuevo equipo de IA',
        meet_team_body_html: 'Tus nuevos compañeros de equipo te ayudarán a crear sitios web, gestionar contactos, encontrar nuevos clientes potenciales, redactar boletines informativos, crear publicaciones para redes sociales, preparar presupuestos y mucho más. <strong class="font-bold">Tú pides. Ellos cumplen.</strong>',
        features_and_more: 'y mucho más',
        footer_trust: 'La plataforma de confianza de miles de empresas de todo el mundo.',
        footer_founded: 'Fundada en Suecia. Disponible en más de 170 países.',
        webinar_duration_minutes: '{n} minutos',
    },
};

const LANG_STORAGE_KEY = 'register-welcome-language';
const availableLanguages = [
    { code: 'en', label: 'EN' },
    { code: 'sv', label: 'SV' },
    { code: 'no', label: 'NO' },
    { code: 'de', label: 'DE' },
    { code: 'es', label: 'ES' },
];

const localizedVideos = {
    en: 'https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-2.mp4',
    sv: 'https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-2.mp4',
    de: 'https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-2-german.mp4',
    no: 'https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-2-norwegian.mp4',
    es: 'https://myflow.fra1.digitaloceanspaces.com/prereg/myflow-onboarding-2.mp4',
};

const storedLanguage = (() => {
    try {
        const v = window.localStorage?.getItem(LANG_STORAGE_KEY);
        return v && messages[v] ? v : null;
    } catch (e) {
        return null;
    }
})();

const userLanguage = ref(storedLanguage);

const activeLanguage = computed(() => {
    if (userLanguage.value && messages[userLanguage.value]) {
        return userLanguage.value;
    }
    const lang = props.welcome?.language ?? 'en';
    return messages[lang] ? lang : 'en';
});

const setLanguage = (code) => {
    if (!messages[code]) return;
    userLanguage.value = code;
    try {
        window.localStorage?.setItem(LANG_STORAGE_KEY, code);
    } catch (e) {
        // ignore
    }
};

const t = (key, params = {}) => {
    let str = messages[activeLanguage.value]?.[key] ?? messages.en[key] ?? key;
    Object.keys(params).forEach(k => {
        str = str.replace(`{${k}}`, params[k]);
    });
    return str;
};

const ctaLabel = computed(() => (props.paidPlan ? t('cta_buy') : t('cta_free')));
const ctaDisclaimer = computed(() =>
    props.paidPlan ? t('cta_disclaimer_paid') : t('cta_disclaimer_free')
);
const showInviterKicker = computed(
    () => !!(props.welcome?.show_sender_name && props.welcome?.sender_name)
);
const showFreePropBanner = computed(
    () => !!(props.welcome?.highlight_free_prop && !props.paidPlan)
);

const registerLink = computed(() => {
    const baseUrl = route('expert.register.shortcut', props.uuid);
    const params = new URLSearchParams({ utm_source: 'info_page' });

    if (props.webinarId) {
        params.set('webinar', props.webinarId);
    }

    return `${baseUrl}?${params.toString()}`;
});

const webinarInfo = computed(() => {
    if (! props.webinarDetails) {
        return null;
    }

    return {
        title: props.webinarDetails.title,
        description: props.webinarDetails.description,
        schedule: props.webinarDetails.start_iso
            ? new Intl.DateTimeFormat(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZoneName: 'short',
              }).format(new Date(props.webinarDetails.start_iso))
            : null,
        language: props.webinarDetails.language
            ? String(props.webinarDetails.language).toUpperCase()
            : null,
        duration: props.webinarDetails.duration_minutes
            ? t('webinar_duration_minutes', { n: props.webinarDetails.duration_minutes })
            : null,
    };
});

// Colourful feature checklist shown under the hero. Labels mirror the wording
// used elsewhere in the product (sidebar / nav locales), keyed per language.
const features = [
    { color: '#7c5cfc', en: 'Webpages',      sv: 'Webbsidor',       no: 'Nettsteder',        de: 'Webseiten',          es: 'Sitios web' },
    { color: '#ec4899', en: 'Newsletters',   sv: 'Nyhetsbrev',      no: 'Nyhetsbrev',        de: 'Newsletter',         es: 'Boletines' },
    { color: '#a855f7', en: 'Agreements',    sv: 'Avtal',           no: 'Avtaler',           de: 'Verträge',           es: 'Acuerdos' },
    { color: '#22c55e', en: 'Quotes',        sv: 'Offerter',        no: 'Tilbud',            de: 'Angebote',           es: 'Presupuestos' },
    { color: '#3b82f6', en: 'CRM',           sv: 'CRM',             no: 'CRM',               de: 'CRM',                es: 'CRM' },
    { color: '#f97316', en: 'Payments',      sv: 'Betalningar',     no: 'Betalinger',        de: 'Zahlungen',          es: 'Pagos' },
    { color: '#3b82f6', en: 'Lead Finder',   sv: 'Lead Finder',     no: 'Lead Finder',       de: 'Lead Finder',        es: 'Buscador de clientes' },
    { color: '#22c55e', en: 'Time Tracking', sv: 'Tidrapportering', no: 'Tidsregistrering',  de: 'Zeiterfassung',      es: 'Control horario' },
    { color: '#3b82f6', en: 'Templates',     sv: 'Mallar',          no: 'Maler',             de: 'Vorlagen',           es: 'Plantillas' },
    { color: '#eab308', en: 'Automations',   sv: 'Automationer',    no: 'Automatiseringer',  de: 'Automatisierungen', es: 'Automatizaciones' },
    { color: '#22c55e', en: 'Mobile App',    sv: 'Mobilapp',        no: 'Mobilapp',          de: 'Mobile App',         es: 'Aplicación móvil' },
    { color: '#7c5cfc', en: 'Forms',         sv: 'Formulär',        no: 'Skjemaer',          de: 'Formulare',          es: 'Formularios' },
    { color: '#ef4444', en: 'Courses',       sv: 'Utbildningar',    no: 'Utdanninger',       de: 'Kurse',              es: 'Cursos' },
    { color: '#a855f7', en: 'Coaching',      sv: 'Coaching',        no: 'Coaching',          de: 'Coaching',           es: 'Coaching' },
    { color: '#14b8a6', en: 'Media Library', sv: 'Mediabibliotek',  no: 'Mediebibliotek',    de: 'Medienbibliothek',   es: 'Biblioteca multimedia' },
];

const localizedFeatures = computed(() =>
    features.map(f => ({
        color: f.color,
        label: f[activeLanguage.value] ?? f.en,
    }))
);

const starterPrompts = [
    {
        key: 'new_customers',
        messageKey: 'flowie_prompt_new_customers',
        bubbleClass: 'bg-aiAgentLeadFinder/20 border-aiAgentLeadFinder/40',
    },
    {
        key: 'new_website',
        messageKey: 'flowie_prompt_new_website',
        bubbleClass: 'bg-aiAgentWebDesigner/20 border-aiAgentWebDesigner/40',
    },
    {
        key: 'send_newsletter',
        messageKey: 'flowie_prompt_send_newsletter',
        bubbleClass:
            'bg-aiAgentNewsletterSpecialist/20 border-aiAgentNewsletterSpecialist/40',
    },
    {
        key: 'create_content',
        messageKey: 'flowie_prompt_create_content',
        bubbleClass:
            'bg-aiAgentContentCreator/20 border-aiAgentContentCreator/40',
    },
    {
        key: 'automatic_emails',
        messageKey: 'flowie_prompt_automatic_emails',
        bubbleClass:
            'bg-aiAgentAutomationExpert/20 border-aiAgentAutomationExpert/40',
    },
    {
        key: 'customer_needs',
        messageKey: 'flowie_prompt_customer_needs',
        bubbleClass:
            'bg-aiAgentRelationshipManager/20 border-aiAgentRelationshipManager/40',
    },
    {
        key: 'time_saving_crm',
        messageKey: 'flowie_prompt_time_saving_crm',
        bubbleClass: 'bg-aiAgentDataAnalyst/20 border-aiAgentDataAnalyst/40',
    },
];

const activeVideo = computed(() =>
    localizedVideos[activeLanguage.value] ?? localizedVideos.en
);

// AI teammates orbiting Flowie in the "Meet your new AI team" section.
const teamMembers = [
    { name: 'Web Designer',          image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/web-designer-v1.webp',          color: '#14b8a6', align: 'left',  pos: 'top-[26%] left-[6%]' },
    { name: 'Lead Finder',           image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/lead-finder-v1.webp',           color: '#ef4444', align: 'left',  pos: 'top-[6%] left-[28%]' },
    { name: 'Automation Expert',     image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/automation-expert-v1.webp',     color: '#111827', align: 'left',  pos: 'top-[48%] left-[2%]' },
    { name: 'Sales Assistant',       image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/sales-assistant-v1.webp',       color: '#16a34a', align: 'left',  pos: 'bottom-[12%] left-[16%]' },
    { name: 'Newsletter Specialist', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/newsletter-specialist-v1.webp', color: '#f97316', align: 'right', pos: 'top-[12%] right-[4%]' },
    { name: 'Content Creator',       image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/content-creator-v1.webp',       color: '#ec4899', align: 'right', pos: 'top-[40%] right-[1%]' },
    { name: 'Data Analyst',          image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/data-analyst-v1.webp',          color: '#3b82f6', align: 'right', pos: 'bottom-[26%] right-[6%]' },
    { name: 'Relationship Manager',  image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/relationship-manager-v1.webp',  color: '#eab308', align: 'right', pos: 'bottom-[6%] right-[22%]' },
];

</script>
<template>
    <div class="bg-white">
        <div
            class="relative z-10 w-full py-2 text-sm font-semibold text-center text-white bg-myflowPurple flex justify-between items-center px-4"
        >
            <div>
                <span
                    v-if="paidPlan"
                >
                    {{ t('paid_banner') }}
                </span>
            </div>
            <div class="flex items-center text-xs font-medium border border-gray-200 rounded-full overflow-hidden">
                <button
                    v-for="lang in availableLanguages"
                    :key="lang.code"
                    type="button"
                    class="px-4 py-0.5 transition-colors text-xxs"
                    :class="activeLanguage === lang.code
                                ? 'bg-myflowLavender text-gray-600'
                                : 'bg-white text-gray-600 hover:bg-gray-50'"
                    @click="setLanguage(lang.code)"
                >
                    {{ lang.label }}
                </button>
            </div>
        </div>

        <div class="max-w-6xl mx-auto px-4 md:px-8 pt-8 pb-20">
            <!-- Header -->
            <header class="flex flex-col-reverse md:flex-row items-start md:justify-between gap-4">
                <div class="flex flex-col items-center md:items-start w-full md:w-auto gap-1">
                    <p
                        v-if="showInviterKicker"
                        class="text-lg md:text-2xl font-bold text-myflowPurple mb-0"
                    >
                        {{ t('invited_by', { name: welcome.sender_name }) }}
                    </p>
                    <span
                        v-if="showFreePropBanner"
                        class="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-800 border border-green-200 rounded-full text-sm font-semibold"
                    >
                        {{ t('free_prop_banner') }}
                    </span>
                </div>
                <div class="flex w-full md:w-auto flex-col items-center gap-3 shrink-0">
                    <div class="flex items-center mb-6 md:mb-0">
                        <template v-if="welcome?.partner_logo_url">
                            <img :src="welcome.partner_logo_url" alt="Partner logo" class="h-10 object-contain" />
                            <div class="h-7 w-px bg-gray-300 mx-3"></div>
                        </template>
                        <img src="https://myflow.fra1.digitaloceanspaces.com/myflow-brand/logo-purple.svg" alt="myflow logo" class="w-28" />
                    </div>
                </div>
            </header>

            <!-- Webinar -->
            <div
                v-if="webinarInfo"
                class="mt-12"
            >
                <div class="p-6 text-left border shadow-sm rounded-xl border-myflowPurple-200 bg-myflowPurple-50 max-w-3xl mx-auto">
                    <p class="mb-1 text-xs font-semibold tracking-wide uppercase text-myflowPurple-700">
                        {{ t('upcoming_webinar') }}
                    </p>
                    <h2 class="text-2xl font-bold md:text-3xl">{{ webinarInfo.title }}</h2>
                    <div
                        v-if="webinarInfo.description"
                        v-html="webinarInfo.description.replace(/\n/g, '<br>')"
                        class="mt-3 text-sm leading-relaxed"
                    ></div>
                    <div class="flex flex-wrap items-center gap-4 mt-4 text-xs font-medium">
                        <span v-if="webinarInfo.schedule">
                            <i class="mr-1 fa-regular fa-calendar"></i>{{ webinarInfo.schedule }}
                        </span>
                        <span v-if="webinarInfo.duration">
                            <i class="mr-1 fa-regular fa-hourglass-half"></i>{{ webinarInfo.duration }}
                        </span>
                        <span v-if="webinarInfo.language">
                            <i class="mr-1 fa-regular fa-globe"></i>{{ webinarInfo.language }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Hero -->
            <div class="grid md:grid-cols-2 items-center gap-12 lg:gap-16 pt-12 md:pt-20">
                <div class="order-2 md:order-1 text-center md:text-left">
                    <h1 class="text-4xl md:text-6xl font-bold leading-none ">
                        {{ t('hero_heading') }}
                    </h1>

                    <div class="mt-6 flex justify-center md:justify-start">
                        <span class="inline-flex font-medium items-center gap-2 px-3 py-1 rounded-full bg-white border border-myflowLavender text-myflowPurple shadow-md shadow-myflowLavender-light">
                            <four-point-star class="w-3 h-3 fill-myflowPurple"></four-point-star>
                            <span v-html="t('hero_badge_html')"></span>
                            <four-point-star class="w-3 h-3 fill-myflowPurple"></four-point-star>
                        </span>
                    </div>

                    <p class="text-lg text-gray-700 mt-6 max-w-lg mx-auto md:mx-0 leading-tight font-light">
                        {{ t('hero_body') }}
                    </p>

                    <div class="mt-8">
                        <a :href="registerLink">
                            <button
                                class="px-5 py-2 text-white rounded-xl bg-myflowPurple text-md"
                            >
                                {{ ctaLabel }}
                            </button>
                        </a>
                        <p v-if="paidPlan" class="text-xs text-gray-500 italic mt-2">
                            {{ ctaDisclaimer }}
                        </p>
                    </div>
                </div>

                <div class="order-2 md:order-1">
                    <div class="hero-laptop max-w-xl mx-auto">
                        <video-player
                            :key="activeVideo"
                            class="hero-laptop-video"
                            :mp4="activeVideo"
                            poster="https://storefront.o3industries.se/myflow-laptop.webp"
                        ></video-player>
                    </div>
                    <p class="text-center text-sm text-gray-500 mt-4">{{ t('video_caption') }}</p>
                </div>
            </div>

            <!-- Feature checklist -->
            <div class="mt-16 md:mt-24 max-w-3xl mx-auto">
                <div class="flex flex-wrap justify-center gap-2 text-sm font-light tracking-wide">
                    <span
                        v-for="feature in localizedFeatures"
                        :key="feature.label"
                        class="inline-flex items-center gap-1.5 rounded-full px-3 py-2 lg:px-4 lg:py-2.5 border border-gray-200 whitespace-nowrap font-medium cursor-default"
                    >
                        <span class="block w-3 h-3 relative">
                            <span
                                class="rounded-full block absolute inset-0 opacity-30"
                                :style="{ backgroundColor: feature.color}"
                            >

                            </span>
                            <span
                                class="rounded-full block absolute z-10 opacity-70"
                                style="top: 3px; left: 3px; right: 3px; bottom: 3px;"
                                :style="{ backgroundColor: feature.color}"
                            >

                            </span>
                        </span>
                        <span>{{ feature.label }}</span>
                    </span>
                    <span class="inline-flex items-center text-gray-500 whitespace-nowrap">
                        {{ t('features_and_more') }}
                    </span>
                </div>
            </div>

            <!-- Flowie quickstart -->
            <div class="mt-16 md:mt-24 max-w-4xl mx-auto">
                <div class="flex flex-col lg:flex-row items-center lg:justify-center lg:space-x-4 space-y-4 lg:space-y-0">
                    <div class="relative">
                        <span class="absolute -top-4 -left-4">
                            <span class="inline-block w-6 relative">
                                <four-point-star class="w-8 h-8 absolute top- fill-myflowPurple"></four-point-star>
                                <four-point-star class="w-6 h-6 absolute -top-4 left-6 fill-myflowPurple"></four-point-star>
                            </span>
                        </span>
                        <ai-agent-ai-orchestrator-avatar class="w-20 h-20 lg:w-28 lg:h-28"></ai-agent-ai-orchestrator-avatar>
                    </div>
                    <h2 class="text-2xl md:text-5xl font-bold text-center flex justify-center max-w-xs md:max-w-lg" style="line-height: 1.2em;">
                        <span>{{ t('flowie_quickstart_heading') }}</span>
                    </h2>
                </div>
                <p class="text-lg text-center text-gray-700 mt-6 max-w-xl mx-auto font-light" v-html="t('flowie_quickstart_html')"></p>

                <div class="mx-auto mt-10 max-w-3xl" aria-label="Flowie example">
                    <div class="flex flex-wrap items-start justify-center gap-2.5">
                        <div
                            v-for="starterPrompt in starterPrompts"
                            :key="starterPrompt.key"
                            class="flowie-example-bubble inline-flex w-fit max-w-[92%] items-center gap-2 border px-3.5 py-2.5 text-left shadow-sm lg:max-w-[78%] cursor-default"
                            :class="starterPrompt.bubbleClass"
                        >
                            <span class="min-w-0 flex-1 text-xs leading-5 text-gray-800 lg:text-sm">
                                {{ t(starterPrompt.messageKey) }}
                            </span>
                            <span
                                class="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-white/70 px-2 py-1 text-[11px] font-semibold text-gray-500 shadow-sm"
                            >
                                <span>{{ t('flowie_prompt_send') }}</span>
                                <svg
                                    class="h-3.5 w-3.5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    <div class="mt-5 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                        <div class="flex items-end gap-2">
                            <textarea
                                rows="1"
                                class="flex-1 resize-none overflow-hidden rounded-lg border border-gray-200 px-4 py-2 text-sm leading-5 text-gray-500 disabled:cursor-default disabled:bg-white disabled:opacity-100"
                                :placeholder="t('flowie_input_placeholder')"
                                disabled
                                tabindex="-1"
                            ></textarea>
                            <button
                                type="button"
                                class="inline-flex h-9 w-9 shrink-0 items-center justify-center text-gray-400 disabled:cursor-default disabled:opacity-100"
                                disabled
                                tabindex="-1"
                            >
                                <svg
                                    class="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                class="inline-flex h-9 items-center justify-center rounded-lg bg-myflowPurple px-4 text-white disabled:cursor-default disabled:opacity-100"
                                disabled
                                tabindex="-1"
                            >
                                <svg
                                    class="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Meet your new AI team -->
            <div class="mt-24 md:mt-32 text-center">
                <h2 class="text-2xl md:text-4xl font-bold flex justify-center">
                    <span>{{ t('meet_team_heading') }}</span>
                    <span class="inline-block w-6 relative">
                        <four-point-star class="w-5 h-5 absolute top- fill-myflowPurple"></four-point-star>
                        <four-point-star class="w-4 h-4 absolute -top-2.5 left-3 fill-myflowPurple"></four-point-star>
                    </span>
                    <!--                    <i class="fa-solid fa-sparkles text-myflowPurple ml-1"></i>-->
                </h2>
                <p class="text-lg text-gray-700 mt-6 max-w-lg mx-auto font-light" v-html="t('meet_team_body_html')"></p>

                <ai-teammates-orbit class="w-full" />

                <div class="mt-12 flex flex-col items-center">
                    <a :href="registerLink">
                        <button
                            class="px-5 py-2 text-white rounded-xl bg-myflowPurple text-md"
                        >
                            {{ ctaLabel }}
                        </button>
                    </a>
                    <p v-if="paidPlan" class="text-xs text-gray-500 italic mt-2">
                        {{ ctaDisclaimer }}
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <footer class="mt-24 md:mt-32 text-center">
                <img
                    src="https://myflow.fra1.digitaloceanspaces.com/myflow-brand/logo-purple.svg"
                    alt="myflow logo"
                    class="w-32 mx-auto"
                />
                <p class="text-sm text-gray-500 mt-6">{{ t('footer_trust') }}</p>
                <p class="text-sm text-gray-500">{{ t('footer_founded') }}</p>
            </footer>
        </div>
    </div>
</template>
<style>
/* Hero laptop / video placeholder */
.flowie-example-bubble {
    border-radius: 18px 18px 18px 8px;
    backdrop-filter: blur(10px);
}

.hero-laptop {
    position: relative;
    background-repeat: no-repeat;
    background-size: contain;
    width: 100%;
    aspect-ratio: 16/9;
}
.hero-laptop-video {
    z-index: 999;
    width: 100%;
    aspect-ratio: 16/9;
}

/* Meet your new AI team */
.team-orb {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(124, 92, 252, 0.18), rgba(124, 92, 252, 0.05) 65%, transparent 75%);
    filter: blur(6px);
    pointer-events: none;
}
@media (min-width: 768px) {
    .team-orb { width: 420px; height: 420px; }
}
.team-member {
    position: absolute;
}
.team-avatar {
    width: 56px;
    height: 56px;
    object-fit: contain;
    animation: teamFloat 4.5s ease-in-out infinite;
}
@media (min-width: 768px) {
    .team-avatar { width: 76px; height: 76px; }
}
.team-member:nth-child(3) .team-avatar { animation-delay: 0.4s; }
.team-member:nth-child(4) .team-avatar { animation-delay: 0.8s; }
.team-member:nth-child(5) .team-avatar { animation-delay: 1.2s; }
.team-member:nth-child(6) .team-avatar { animation-delay: 0.6s; }
.team-member:nth-child(7) .team-avatar { animation-delay: 1s; }
.team-member:nth-child(8) .team-avatar { animation-delay: 0.3s; }
.team-member:nth-child(9) .team-avatar { animation-delay: 0.9s; }
@keyframes teamFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
}
</style>
